import { MapConstants } from '../../MapConstants';
import { IRoom } from '../../Models/IRoom';
import { IRoomItem, RoomItemType } from '../../Models/IRoomItem';
import { CollisionRoomUtils } from '../Collisions/CollisionRoomUtils';
import { PointUtils } from '../PointUtils';
import { qSVG } from '../qSVG';
import { CoordPoint, Wall } from '../Types';
import { WallResizeDirection, WallUtils } from '../Wall/WallUtils';
import { PolylabelUtils } from '../PolylabelUtils';
import { JstsUtils } from '../JstsUtils';

export const RoomItemConstants = {
    RoomItemInConstruction: null,
    PreviousNearestWall: null,
    PreviousHorVNearestWall: null,

    ROOM_ITEM_MIN_SIZE: 20,
    ROOM_ITEM_MIN_CONTACT_LENGTH: 20,
    ROOM_ITEM_DIVIDER_MIN_SIZE: 7,
    ROOM_ITEM_MAX_SIZE: 3999,
};

export type RoomItemLimits = {
    horizontal: { min: number; max: number };
    vertical: { min: number; max: number };
};

export class RoomItemUtils {
    public static toPosition = (roomItem: IRoomItem): CoordPoint | undefined => {
        if (roomItem.graphTranslation) {
            return { x: roomItem.graphTranslation.x, y: roomItem.graphTranslation.y };
        }
        return undefined;
    };

    public static updateRoomItems = (room: IRoom, roomItems: Array<IRoomItem>) => {
        room.roomItems = roomItems.filter((x) => x.roomId === room.roomId);
    };

    public static calcRoomItemMinContactLength(roomItemType?: RoomItemType) {
        return roomItemType === RoomItemType.Cupboard ? RoomItemConstants.ROOM_ITEM_MIN_CONTACT_LENGTH : 0;
    }

    public static roomItemNearestWall(walls: Array<Wall>, snap: CoordPoint, roomItemType?: RoomItemType) {
        const nearestWall = WallUtils.nearWallFrom(
            snap,
            Infinity,
            walls,
            MapConstants.PreviousNearestWall,
            roomItemType === 'cupboard'
        )!;
        MapConstants.PreviousNearestWall = nearestWall?.wall;
        if (nearestWall && WallUtils.isWallHorV(nearestWall.wall!)) {
            MapConstants.PreviousHorVNearestWall = nearestWall.wall;
        }
        return nearestWall;
    }

    /** return true if the room item is inside the room (or in contact with the room for cupboards) */
    public static isRoomItemInsideRoomOrInContactWithRoom(
        roomItem: IRoomItem,
        room: IRoom,
        checkIntersection: boolean = true
    ) {
        const isRoomItemContainedInRoom = CollisionRoomUtils.testIfPolygonAContainedInPolygonB(
            roomItem.coordsReal?.slice(0, -1),
            room.coords?.slice(0, -1),
            0,
            1
        );

        //* Cupboards can intersect with a wall
        if (!isRoomItemContainedInRoom && roomItem.type === RoomItemType.Cupboard) {
            const roomItemPoly = JstsUtils.createJstsPolygon(roomItem.coordsReal!).buffer(1);
            const roomPoly = JstsUtils.createJstsPolygon(room.coords);

            if (roomItemPoly.overlaps(roomPoly)) {
                return false;
            }

            return true;
        }

        return checkIntersection
            ? !isRoomItemContainedInRoom && PointUtils.testPolygonsIntersect(room.coords, roomItem.coordsReal)
            : !isRoomItemContainedInRoom;
    }

    public static roomItemMoveSide(
        roomItem: IRoomItem,
        sideIndex: number,
        snap: CoordPoint,
        initialSnap: CoordPoint,
        initialSideStartPoint: CoordPoint,
        initialSideEndPoint: CoordPoint
    ) {
        const translationVector = qSVG.vectorXY(initialSnap, snap);
        const newWallMoveStart = PointUtils.translation(initialSideStartPoint, translationVector);
        const newWallMoveEnd = PointUtils.translation(initialSideEndPoint, translationVector);
        if (sideIndex === 0) {
            roomItem.coordsReal![4] = newWallMoveStart;
            roomItem.coordsReal![0] = newWallMoveStart;
            roomItem.coordsReal![1] = newWallMoveEnd;
        } else if (sideIndex === 1) {
            roomItem.coordsReal![1] = newWallMoveStart;
            roomItem.coordsReal![2] = newWallMoveEnd;
        } else if (sideIndex === 2) {
            roomItem.coordsReal![2] = newWallMoveStart;
            roomItem.coordsReal![3] = newWallMoveEnd;
        } else if (sideIndex === 3) {
            roomItem.coordsReal![3] = newWallMoveStart;
            roomItem.coordsReal![4] = newWallMoveEnd;
            roomItem.coordsReal![0] = newWallMoveEnd;
        }

        return { roomItem };
    }

    public static roomItemCoordsRealToCoords(width: number, height: number) {
        return [
            { x: -width / 2, y: -height / 2 },
            { x: width / 2, y: -height / 2 },
            { x: width / 2, y: height / 2 },
            { x: -width / 2, y: height / 2 },
            { x: -width / 2, y: -height / 2 },
        ];
    }

    public static roomItemCoordsRealToGraphTranslation(
        coordsReal: Array<CoordPoint> = [],
        width: number,
        height: number
    ) {
        return { x: coordsReal[0].x + width / 2, y: coordsReal[0].y + height / 2 };
    }

    public static getRoomItemSizeLimits(type: RoomItemType): RoomItemLimits {
        let hMinValue = RoomItemConstants.ROOM_ITEM_MIN_SIZE;
        const hMaxValue = RoomItemConstants.ROOM_ITEM_MAX_SIZE;

        let vMinValue = RoomItemConstants.ROOM_ITEM_MIN_SIZE;
        const vMaxValue = RoomItemConstants.ROOM_ITEM_MAX_SIZE;

        if (type === RoomItemType.Divider) {
            hMinValue = RoomItemConstants.ROOM_ITEM_DIVIDER_MIN_SIZE;
            vMinValue = RoomItemConstants.ROOM_ITEM_DIVIDER_MIN_SIZE;
        }

        return {
            horizontal: { min: hMinValue, max: hMaxValue },
            vertical: { min: vMinValue, max: vMaxValue },
        };
    }

    //! temporary. Change IRoomItem to class
    public static getRoomItemPartialWalls(roomItem: IRoomItem) {
        const walls = [];
        for (let i = 0; i < roomItem.coordsReal!.length - 1; i++) {
            const wallStart = roomItem.coordsReal![i];
            const wallEnd = roomItem.coordsReal![i + 1];
            walls.push({
                start: wallStart,
                end: wallEnd,
                angleRad: Math.atan2(wallEnd.y - wallStart.y, wallEnd.x - wallStart.x),
                equations: { base: qSVG.createEquation(wallStart.x, wallStart.y, wallEnd.x, wallEnd.y) },
            });
        }
        return walls;
    }

    //! temporary
    public static getRoomItemPartialWall(roomItem: IRoomItem, sideIndex: number) {
        const start = roomItem.coordsReal![sideIndex];
        const end = roomItem.coordsReal![sideIndex + 1];
        return {
            start,
            end,
            angleRad: qSVG.getAngle(start, end).rad,
        };
    }

    public static getCoords(roomItem: IRoomItem) {
        const centroid = PolylabelUtils.polylabel([roomItem.coordsReal?.map((p) => [p.x, p.y])], 1.0);
        const coords = roomItem.coordsReal!.map((point) => {
            return { x: point.x - centroid.x!, y: point.y - centroid.y! };
        });
        return coords;
    }

    public static moveRoomItemToNewRoom = (roomItem: IRoomItem, rooms: Array<IRoom>, destinationRoomId: string) => {
        const sourceRoom = rooms.find((x) => x.roomId === roomItem.roomId);
        const destinationRoom = rooms.find((x) => x.roomId === destinationRoomId);
        sourceRoom?.roomItems?.splice(
            sourceRoom.roomItems.findIndex((x) => x.roomItemId === roomItem.roomItemId),
            1
        );
        destinationRoom?.roomItems?.push(roomItem);
        roomItem.roomId = destinationRoomId;
    };

    //fonction impure
    public static resizeSide = (
        roomItemToResize: IRoomItem,
        sideIndex: number,
        size: number,
        direction: WallResizeDirection
    ) => {
        const wallToResize = RoomItemUtils.getRoomItemPartialWall(roomItemToResize, sideIndex);
        let newStart;
        let newEnd;
        switch (direction) {
            case WallResizeDirection.FixedStart:
                newStart = wallToResize.start;

                newEnd = {
                    x: wallToResize.start.x + Math.cos(wallToResize.angleRad) * size,
                    y: wallToResize.start.y + Math.sin(wallToResize.angleRad) * size,
                };
                break;

            case WallResizeDirection.FixedEnd:
                newStart = {
                    x: wallToResize.end.x - Math.cos(wallToResize.angleRad) * size,
                    y: wallToResize.end.y - Math.sin(wallToResize.angleRad) * size,
                };

                newEnd = wallToResize.end;
                break;

            case WallResizeDirection.Both:
            default:
                const centerWallToResize = PointUtils.bboxCenter([wallToResize.start, wallToResize.end]);

                newStart = {
                    x: centerWallToResize.x - (Math.cos(wallToResize.angleRad) * size) / 2,
                    y: centerWallToResize.y - (Math.sin(wallToResize.angleRad) * size) / 2,
                };

                newEnd = {
                    x: centerWallToResize.x + (Math.cos(wallToResize.angleRad) * size) / 2,
                    y: centerWallToResize.y + (Math.sin(wallToResize.angleRad) * size) / 2,
                };
                break;
        }

        if (sideIndex === 0) {
            roomItemToResize.coordsReal![4] = newStart;
            roomItemToResize.coordsReal![0] = newStart;
            roomItemToResize.coordsReal![1] = newEnd;
        } else if (sideIndex === 3) {
            roomItemToResize.coordsReal![3] = newStart;
            roomItemToResize.coordsReal![4] = newEnd;
            roomItemToResize.coordsReal![0] = newEnd;
        } else {
            roomItemToResize.coordsReal![sideIndex] = newStart;
            roomItemToResize.coordsReal![sideIndex + 1] = newEnd;
        }
    };
}
